import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { DEFAULT_AUDIT_ORDER, listAuditRecords } from "./audit.gateway";
import type { AuditFilters, AuditQueryState, AuditSortOrder } from "./audit.types";

export const AUDIT_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os registros de auditoria do Hub.";
export const AUDIT_ERROR_MESSAGE = "Não foi possível carregar os registros de auditoria.";

const INITIAL_STATE: AuditQueryState = {
  status: "loading",
  records: [],
  total: 0,
  errorMessage: null,
};

export interface UseAuditRecordsParams {
  filters: AuditFilters;
  page: number;
  pageSize: number;
  order?: AuditSortOrder;
}

interface UseAuditRecordsResult extends AuditQueryState {
  reload: () => void;
}

export function useAuditRecords({
  filters,
  page,
  pageSize,
  order = DEFAULT_AUDIT_ORDER,
}: UseAuditRecordsParams): UseAuditRecordsResult {
  const [state, setState] = useState<AuditQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  const { status, ano, alteracao, modulo, responsavelId } = filters;

  useEffect(() => {
    let cancelled = false;
    // Cancela a consulta anterior: paginar ou filtrar de novo descarta a resposta em trânsito.
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    listAuditRecords({
      status,
      ano,
      alteracao,
      modulo,
      responsavelId,
      pagina: page,
      tamanhoPagina: pageSize,
      ordem: order,
      signal: controller.signal,
    })
      .then(({ records, total }) => {
        if (cancelled) return;
        setState({ status: "success", records, total, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;

        const isForbidden = isAPIError(error) && error.status === 403;
        setState({
          status: isForbidden ? "forbidden" : "error",
          records: [],
          total: 0,
          errorMessage: isForbidden
            ? AUDIT_FORBIDDEN_MESSAGE
            : getErrorMessage(error) || AUDIT_ERROR_MESSAGE,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [status, ano, alteracao, modulo, responsavelId, page, pageSize, order, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
